import React, { useEffect, useState } from 'react';
import { ExamsResolutionList } from './exams-resolution-list';
import toast from 'react-hot-toast';
import { useQuery } from 'jsonapi-react';
import { useTranslation } from 'react-i18next';
import { MockExams } from '@/pages/mock-exams';
import Onboarding from '@/components/custom/onboarding';
import { useSession } from '@/hooks/useSession';
import { ExamsMetricsUser } from '@/pages/exams/exams-metrics/exams-metrics-user';
import { AdminMetrics } from '@/pages/exams/exams-metrics/AdminMetrics';
import { useLocation, useNavigate } from 'react-router-dom';
import { DashboardContent } from '@/layouts/dashboard';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { usePlatformAbilities } from '@/hooks/usePlatformAbilities';
import usePlayfulStudent from '@/hooks/usePlayfulStudent';
import tiaPensando from '@/assets/images/tia/TIA-PENSANDO.png';
import { TiaBanner } from '@/components/custom/tia-banner';

const TAB_ROUTES = {
  simulados: '/simulados',
  avaliacoes: '/avaliacoes',
  resultados: '/avaliacoes/meus-resultados'
};

const getActiveTabValue = path => {
  if (path.includes(TAB_ROUTES.resultados)) return 'resultados';
  if (path.includes(TAB_ROUTES.avaliacoes)) return 'avaliacoes';
  if (path.includes(TAB_ROUTES.simulados)) return 'simulados';
  return 'simulados';
};

export function ExamsResolution({ uri }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { session } = useSession();
  const { abilities } = usePlatformAbilities();
  const playfulStudent = usePlayfulStudent();
  const sessionUser = session.user.profileNames[0];
  const [showOnboarding, setShowOnboarding] = useState(false);
  const { data: courses = [], error: errorCourses, isLoading: isLoadingCourses } = useQuery('courses/simple');
  const { data: exams = [], error: errorExams, isLoading: isLoadingExams } = useQuery('question_books?filter[visibility]=exam&filter[except_learning_systems]=true');

  const profiles = session?.user?.profileNames || (session?.user && session?.user['profile-names']) || [];
  const isLoading = isLoadingCourses || isLoadingExams;

  useEffect(() => {
    if (errorCourses) {
      toast.error(t('toast.errorGetCourse'));
    }
    if (errorExams) {
      toast.error(t('toast.errorGetExams'));
    }
  }, [errorCourses, errorExams]);

  const activeTabValue = getActiveTabValue(location.pathname);

  const breadcrumbs = {
    simulados: {
      title: 'Avaliações e Questões',
      nav: [
        {
          route: uri,
          name: 'Simulados',
          isActive: true
        }
      ]
    },
    avaliacoes: {
      title: 'Avaliações e Questões',
      nav: [
        {
          route: uri,
          name: 'Provas',
          isActive: true
        }
      ]
    },
    resultados: {
      title: 'Avaliações e Questões',
      nav: [
        {
          route: uri,
          name: 'Meus resultados',
          isActive: true
        }
      ]
    }
  };

  // Aba ativa chanfrada (igual à tela de perfil): laranja + texto branco + lip escuro.
  const playfulTab = playfulStudent
    ? 'data-[state=active]:bg-[var(--color-mission)] data-[state=active]:text-white data-[state=active]:font-extrabold data-[state=active]:shadow-[0_3px_0_var(--color-mission-dark)] data-[state=active]:-translate-y-px'
    : undefined;

  const tabTriggers = (
    <>
      {abilities?.['avaliacoes-simulados'] && (
        <TabsTrigger
          value="simulados"
          className={playfulTab}
          onClick={() => navigate(TAB_ROUTES.simulados)}
        >
          {t('textsCommon.simulations')}
        </TabsTrigger>
      )}
      {abilities?.['avaliacoes-provas'] && (
        <TabsTrigger
          value="avaliacoes"
          className={playfulTab}
          onClick={() => navigate(TAB_ROUTES.avaliacoes)}
        >
          {t('exams.toggleExams')}
        </TabsTrigger>
      )}
      {abilities?.avaliacoes && (
        <TabsTrigger
          value="resultados"
          className={playfulTab}
          onClick={() => navigate(TAB_ROUTES.resultados)}
        >
          {t('exams.toggleMyResults')}
        </TabsTrigger>
      )}
    </>
  );

  return (
    <DashboardContent
      variant="plain"
      breadcrumbs={breadcrumbs[activeTabValue]}
    >
      <Tabs value={activeTabValue}>
        {playfulStudent && (
          <TiaBanner
            className="mb-6 md:mt-16"
            image={tiaPensando}
            imageHeight={180}
            imageOffset={150}
            bubbleMaxWidth={520}
            text='Aqui é a área de Simulados e Provas! Treine pro SAEB e acompanhe seus resultados em "Meus resultados". Bora praticar? 📝'
          />
        )}

        {playfulStudent ? <TabsList className="mb-8 border border-neutral-lighter bg-white">{tabTriggers}</TabsList> : <TabsList className="mb-4">{tabTriggers}</TabsList>}

        {/* Conteúdo da Aba Simulados */}
        <TabsContent value="simulados">
          <MockExams courses={courses} />
        </TabsContent>

        {/* Conteúdo da Aba Avaliações */}
        <TabsContent value="avaliacoes">
          <ExamsResolutionList
            courses={courses}
            exams={exams}
            loading={isLoading}
          />
        </TabsContent>

        {/* Conteúdo da Aba Meus Resultados */}
        <TabsContent value="resultados">
          {/* Lógica condicional para Admin/Aluno */}
          {sessionUser === 'Aluno' ? <ExamsMetricsUser courses={courses} /> : <AdminMetrics />}
        </TabsContent>
      </Tabs>

      <Onboarding
        show={showOnboarding}
        setShowOnboarding={setShowOnboarding}
        onClose={() => setShowOnboarding(false)}
        path={location.pathname}
        profiles={profiles}
        title={location.pathname === TAB_ROUTES.simulados ? t('onboarding.titleMockQuestionsBook') : t('onboarding.titleExamsResolution')}
      />
    </DashboardContent>
  );
}
